<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { t, tn } from '../i18n';
import { displayName, type ProfileRecord } from '../profiles/registry';
import { canvasCount, deleteProfile } from '../profiles/session';
import ProfileAvatar from './ProfileAvatar.vue';

/**
 * Deleting a profile with all its data (docs/TZ-PROFILES.md 4.6): what goes, an offer to export it
 * first (inside the profile), and the profile's name typed to confirm.
 */
const props = defineProps<{ profile: ProfileRecord; canExport?: boolean }>();
const emit = defineEmits<{ done: [folderOk: boolean]; cancel: []; export: [] }>();

const typed = ref('');
const busy = ref(false);
const count = ref<number | null>(null);
const name = computed(() => displayName(props.profile));
const matches = computed(() => typed.value.trim().toLocaleLowerCase() === name.value.toLocaleLowerCase());

onMounted(async () => {
  count.value = await canvasCount(props.profile).catch(() => null);
});

async function remove(): Promise<void> {
  if (!matches.value || busy.value) return;
  busy.value = true;
  try {
    emit('done', await deleteProfile(props.profile));
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <form class="del" data-dialog="profile-delete" @submit.prevent="remove">
    <ProfileAvatar :id="profile.id" :first-name="profile.firstName" :last-name="profile.lastName" :avatar="profile.avatar" :size="56" />
    <h2>{{ t('profiles.deleteTitle', { name }) }}</h2>
    <p class="muted">
      {{ count === null ? t('profiles.deleteWhatUnknown') : tn('profiles.deleteWhat', count) }}
      <template v-if="profile.storage.kind === 'folder'"> {{ t('profiles.deleteFolder', { folder: profile.storage.name }) }}</template>
    </p>
    <button v-if="canExport" type="button" class="pill tall" data-act="export-first" @click="emit('export')">{{ t('profiles.exportFirst') }}</button>
    <label class="confirm">
      <span>{{ t('profiles.deleteType', { name }) }}</span>
      <input v-model="typed" class="input" type="text" autocomplete="off" :placeholder="name" data-field="confirm-name" />
    </label>
    <div class="actions">
      <button type="button" class="pill tall" @click="emit('cancel')">{{ t('common.cancel') }}</button>
      <button type="submit" class="pill tall danger-btn" :disabled="!matches || busy" data-act="delete-profile">{{ t('profiles.deleteForever') }}</button>
    </div>
  </form>
</template>

<style scoped>
.del {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
h2 {
  margin: 0;
  font-size: 18px;
}
p {
  margin: 0;
  line-height: 1.5;
}
.confirm {
  display: grid;
  gap: 6px;
  width: 100%;
  text-align: left;
  font-size: 13px;
}
.input {
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--bg);
  color: var(--text);
  font: inherit;
}
.actions {
  display: flex;
  gap: 8px;
}
.danger-btn {
  background: var(--danger);
  color: #fff;
  border-color: transparent;
}
.danger-btn:disabled {
  opacity: 0.45;
}
</style>
